import { getToken } from '../Context/User.context';
import API from './index';

const PING = 'ping';
const RETRY_DELAY = 5000;
const DEFAULT_POLL_INTERVAL = 10000;

/**
 * Abonnement temps réel aux checklists (SSE + repli polling).
 *
 * `EventSource` ne permet pas d'envoyer le header `Authorization` : le flux
 * est donc lu via `fetch`. Si le flux tombe (proxy, réseau...), un polling
 * régulier déclenche `onEvent({ type: 'poll' })` pour recharger la vue.
 */
export const subscribeChecklistEvents = (
  onEvent: (event: { type: string; instanceId?: number }) => void,
  pollInterval: number = DEFAULT_POLL_INTERVAL,
) => {
  const baseUrl = (API.defaults.baseURL || '').replace(/\/$/, '');
  let stopped = false;
  let controller: AbortController | null = null;
  let pollTimer: any = null;
  let retryTimer: any = null;

  const startPolling = () => {
    if (pollTimer || stopped) return;
    pollTimer = setInterval(() => onEvent({ type: 'poll' }), pollInterval);
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const handleChunk = (chunk: string) => {
    const data = chunk
      .split('\n')
      .filter((line: string) => line.startsWith('data:'))
      .map((line: string) => line.replace(/^data:\s?/, ''))
      .join('\n');
    if (!data) return;
    try {
      const event = JSON.parse(data);
      if (event && event.type && event.type !== PING) {
        onEvent(event);
      }
    } catch (error) {
      // Charge utile non JSON : ignorée.
    }
  };

  const connect = async () => {
    if (stopped) return;
    controller = new AbortController();
    try {
      const response = await fetch(`${baseUrl}/checklist/events`, {
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${getToken()}`,
        },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`SSE unavailable (${response.status})`);
      }
      stopPolling();
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done || stopped) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        chunks.forEach(handleChunk);
      }
    } catch (error) {
      // Flux interrompu : le polling prend le relais ci-dessous.
    }
    if (!stopped) {
      startPolling();
      retryTimer = setTimeout(connect, RETRY_DELAY);
    }
  };

  connect();

  return () => {
    stopped = true;
    stopPolling();
    if (retryTimer) {
      clearTimeout(retryTimer);
    }
    if (controller) {
      controller.abort();
    }
  };
};
